import { Trans, useTranslation } from 'react-i18next';
import { Button, InputAdornment, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { ModalTitleWrapper } from './styles';
import { useDownloadRepaymentSummaryWireFileMutation } from 'src/shared/api/company';
import { Step } from 'src/modules/SaldoView/components/CompanySelectorBar/components/Step';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import { Form } from 'react-final-form';
import { parseLocaleNumber, formatCurrency } from 'src/shared/hooks/useCurrencyMask';
import { TextField } from 'mui-rff';
import { getCurrencySymbol } from 'src/shared/utils/currency';

export type RepaymentSummaryWireRequestModalProp = {
  closeModal: () => void;
  selectedCompanyId: string;
};

export const RepaymentSummaryWireRequestModal = ({
  closeModal,
  selectedCompanyId
}: RepaymentSummaryWireRequestModalProp) => {
  const { t } = useTranslation('overview');
  const [downloadRepaymentSummaryWireFile] = useDownloadRepaymentSummaryWireFileMutation();

  const validateAmount = (value: string) => {
    if (!value || value.trim() === '') {
      return t('messages.field is required', { ns: 'form' });
    }

    const numericValue = parseLocaleNumber(value);

    // If parsing fails (invalid format), treat as empty - formatCurrency handles the filtering
    if (numericValue === null || numericValue === undefined) {
      return undefined;
    }

    // If we have a valid number, check the range
    if (numericValue < 1 || numericValue > 1000000) {
      const currencySymbol = getCurrencySymbol();
      return t('messages.field value must be between', { ns: 'form', min: 1, max: `1.000.000 ${currencySymbol}` });
    }

    return undefined;
  };

  const handleSave = async (values: any) => {
    await downloadRepaymentSummaryWireFile({
      companyId: selectedCompanyId,
      amount: parseLocaleNumber(values.amount) ?? 0
    });
    closeModal();
  };

  return (
    <Grid2>
      <ModalTitleWrapper>{t('self funding.generate invoice')}</ModalTitleWrapper>
      <Typography mb={2} sx={{ textAlign: 'left' }}>
        <Trans i18nKey={'self funding.generate invoice description'} ns="overview" />
      </Typography>
      <Form onSubmit={handleSave}>
        {({ handleSubmit, valid, values }) => {
          // Check if we have a valid numeric value
          const numericValue = values?.amount ? parseLocaleNumber(values.amount) : null;
          const hasValidNumericValue =
            numericValue !== null && numericValue !== undefined && numericValue >= 1 && numericValue <= 1000000;

          const isSubmitDisabled = !valid || !hasValidNumericValue;

          return (
            <>
              <Grid2>
                <Step
                  step={<DescriptionIcon sx={{ position: 'relative', top: 2 }} />}
                  title="self funding.generate invoice step description"
                >
                  <TextField
                    name="amount"
                    label={t('field.amount')}
                    InputProps={{
                      style: { width: '100%' },
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                    fieldProps={{
                      format: formatCurrency,
                      validate: validateAmount
                    }}
                  />
                </Step>
              </Grid2>
              <Grid2 mt={2} sx={{ textAlign: 'right' }}>
                <Button variant="text" onClick={closeModal}>
                  {t('cancel', { ns: 'common' })}
                </Button>
                <Button onClick={handleSubmit} type="submit" variant="contained" disabled={isSubmitDisabled}>
                  {t('generate', { ns: 'common' })}
                </Button>
              </Grid2>
            </>
          );
        }}
      </Form>
    </Grid2>
  );
};
